import React, { useCallback, useMemo, useState } from "react"
import { Button, Select } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileCsv, faLink, faLinkSlash, faCrown } from "@fortawesome/free-solid-svg-icons"

import { useProjectsQuery } from "features/Projects/api/useProjectsQuery"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import { notify } from "shared/ui/Notifications/notify"
import { ACCENT } from "constants/brand"
import { TrackerProject, TrackerProjectsResult } from "types/questionnaire.types"
import { PROJECT_FILTER, PROJECT_TYPE } from "types/project.types"
import { exportToCsv } from "utils/exportToCsv"
import { failureOf } from "utils/errMsgOf"
import { parseServerDate } from "utils/serverDate"
import {
  useAddToTracker,
  useRemoveFromTracker,
  useTrackerQuery,
  useTrackers,
} from "../api/useTrackers"
import { withoutCodes } from "../utils/historyLabels"

// Why the tracker couldn't be read, in words, or null when it could
const trackerUnreadable = (data: TrackerProjectsResult | undefined): string | null => {
  const status = data?.trackerStatus
  if (!status || status === "ok") return null
  if (status === "no_access")
    return "Couldn’t read this project’s tracker: you can’t open its root project."
  return "Couldn’t read this project’s tracker right now."
}

// When a member last had something recorded in its history, as a time to sort by; members
// without one (not opened) sort last
const lastRecordedTime = (m: TrackerProject): number =>
  parseServerDate(m.lastRecordedAt ?? "")?.getTime() ?? Number.POSITIVE_INFINITY

interface TrackerManagerProps {
  projectId: string
  projectName?: string
}

/**
 * Provenance ▸ Tracker. Manage the group of projects treated as waves of one study, and export the
 * wave-to-wave metadata report. Linking is anchored on THIS project: a project you add joins this
 * project's tracker (and this project becomes the root if it's the first). Mutations are confirmed,
 * since linking/unlinking moves a project between trackers.
 */
const TrackerManager: React.FC<TrackerManagerProps> = ({ projectId, projectName }) => {
  const tracker = useTrackerQuery(projectId)
  const { mutate: addWave, isPending: adding } = useAddToTracker()
  const { mutate: removeWave, isPending: removing } = useRemoveFromTracker()
  const { compareById } = useTrackers()

  // Candidate projects to link as waves (owned or member).
  const { data: projectsData } = useProjectsQuery(PROJECT_FILTER.BOTH)
  const projects = useMemo(() => projectsData ?? [], [projectsData])

  const members = useMemo(() => tracker.data?.members ?? [], [tracker.data])
  const [toAdd, setToAdd] = useState<string | undefined>(undefined)
  const [exporting, setExporting] = useState(false)
  // Until the tracker is read, nothing can be added or removed: who becomes the root depends on it
  const loading = tracker.isLoading
  const unreadable = trackerUnreadable(tracker.data)
  const unread = loading || tracker.isError || unreadable !== null
  const busy = exporting || adding || removing || unread
  // What's pending confirmation (the app's ConfirmationModal drives add / unlink / leave).
  const [confirm, setConfirm] = useState<{ kind: "add" | "remove"; member?: TrackerProject } | null>(null)

  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members])
  const options = useMemo(
    () =>
      projects
        .filter(
          (p) =>
            p.id !== projectId &&
            !memberIds.has(p.id) &&
            (p.metadata?.Type || "").toLowerCase() === PROJECT_TYPE.SURVEY
        )
        .map((p) => ({ value: p.id, label: p.metadata?.Name || p.name || p.id })),
    [projects, projectId, memberIds]
  )

  // The picked project joins THIS project's tracker (this project anchors it / becomes the root).
  // A failure is reported by the mutation; the list is read again either way.
  const handleAdd = useCallback(() => {
    const other = toAdd ? projects.find((p) => p.id === toAdd) : undefined
    if (!other) return
    addWave(
      { wave: { id: other.id, owner: other.owner }, projectId },
      {
        onSuccess: () => {
          notify.toast.success(
            `Added ${other.metadata?.Name || other.name || other.id} as a wave of this tracker.`
          )
          setToAdd(undefined)
        },
      }
    )
  }, [toAdd, projects, addWave, projectId])

  const handleRemove = useCallback(
    (m: TrackerProject) => removeWave({ wave: { id: m.id, owner: m.owner }, projectId }),
    [removeWave, projectId]
  )

  // Wave-to-wave metadata report across the whole tracker, as CSV: the waves ordered by when
  // each last had something recorded, oldest first (the list order when two are the same).
  const handleExportReport = useCallback(async () => {
    if (members.length < 2) return
    setExporting(true)
    try {
      const ordered = members
        .map((m, i) => ({ m, i, ts: lastRecordedTime(m) }))
        .sort((a, b) => a.ts - b.ts || a.i - b.i)
        .map((x) => x.m)

      const rows: Record<string, string>[] = []
      let failed = 0
      for (let i = 1; i < ordered.length; i += 1) {
        const base = ordered[i - 1]
        const target = ordered[i]
        const fromWave = base.name || base.id.slice(0, 8)
        const toWave = target.name || target.id.slice(0, 8)
        const stub = { fromWave, toWave, changeKind: "", field: "", path: "", oldValue: "", newValue: "" }
        try {
          const res = await compareById(base.id, base.owner, target.id, target.owner)
          const data = res.data
          if (!data || data.parseStatus === "parse_failed") {
            failed += 1
            rows.push({ ...stub, status: "could not parse" })
          } else if (data.parseStatus === "no_qnr") {
            rows.push({ ...stub, status: "no questionnaire to compare" })
          } else if (data.parseStatus !== "ok") {
            failed += 1
            rows.push({ ...stub, status: "could not compare" })
          } else if (data.identical || !data.changes?.length) {
            rows.push({ ...stub, status: "in sync" })
          } else {
            // Every change, but never an answer code
            for (const c of data.changes.map(withoutCodes)) {
              rows.push({
                fromWave,
                toWave,
                status: "changed",
                changeKind: c.kind,
                field: c.label || c.path,
                path: c.path,
                oldValue: c.oldValue ?? "",
                newValue: c.newValue ?? "",
              })
            }
          }
        } catch (e) {
          // The row says why, so a failed pair is never mistaken for one in sync
          failed += 1
          rows.push({ ...stub, status: `compare failed: ${failureOf(e)}` })
        }
      }

      if (!rows.length) {
        notify.toast.warning("Nothing to export: no two waves could be compared.")
        return
      }
      const date = new Date().toISOString().split("T")[0]
      exportToCsv(rows, `tracker-report-${projectName || projectId}-${date}`, {
        columns: ["fromWave", "toWave", "status", "changeKind", "field", "path", "oldValue", "newValue"],
        headers: ["From wave", "To wave", "Status", "Change kind", "Field", "Path", "Old value", "New value"],
      })
      if (failed > 0)
        notify.toast.warning(
          `${failed} pair${failed === 1 ? "" : "s"} of waves couldn’t be compared. The report says which.`
        )
    } catch (e) {
      notify.error("Error Exporting Tracker Report!", e instanceof Error ? e.message : "")
    } finally {
      setExporting(false)
    }
  }, [members, compareById, projectId, projectName])

  const onConfirm = useCallback(() => {
    if (!confirm) return
    if (confirm.kind === "add") handleAdd()
    else if (confirm.member) handleRemove(confirm.member)
    setConfirm(null)
  }, [confirm, handleAdd, handleRemove])

  const confirmMember = confirm?.member
  const isLeave = confirmMember?.id === projectId

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      {/* Waves + linking */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Tracker: waves of this study
        </div>
        <p className="text-[12px] leading-relaxed text-slate-400">
          A tracker groups projects as waves of one study so you can compare them over time. One wave is
          the <span className="font-medium">root</span> (the base). Adding a wave links it into{" "}
          <span className="font-medium">this</span> project’s tracker.
        </p>

        {loading ? (
          <div className="mt-2 flex justify-center">
            <Loader fontSize={24} color={ACCENT} />
          </div>
        ) : tracker.isError || unreadable ? (
          <p className="mt-2 text-[12px] font-medium text-rose-600">
            {unreadable ?? "Couldn’t load this project’s tracker."}{" "}
            <button type="button" onClick={() => void tracker.refetch()} className="underline">
              Try again
            </button>
          </p>
        ) : members.length === 0 ? (
          <p className="mt-2 text-[12px] text-slate-500">
            Not part of a tracker yet. Add a wave below to start one; this project becomes the root.
          </p>
        ) : (
          <ul className="mt-2 space-y-1">
            {members.map((m) => (
              <li key={m.id} className="flex items-center justify-between text-[12px]">
                <span className="flex items-center gap-1.5">
                  {m.isRoot && (
                    <FontAwesomeIcon icon={faCrown} className="h-3 w-3 text-amber-400" title="root (base wave)" />
                  )}
                  <span className={m.id === projectId ? "font-medium text-slate-700" : "text-slate-600"}>
                    {m.name || m.id}
                    {m.id === projectId ? " · this project" : ""}
                    {m.isRoot ? " · root" : ""}
                  </span>
                  {m.resolved === false && (
                    <span className="text-[11px] text-slate-400" title="You can’t open this project, so only its ID is shown.">
                      · can’t be opened
                    </span>
                  )}
                </span>
                {!m.isRoot && (
                  <Button
                    danger
                    disabled={busy}
                    onClick={() => setConfirm({ kind: "remove", member: m })}
                    icon={<FontAwesomeIcon icon={faLinkSlash} className="h-3 w-3" />}
                  >
                    {m.id === projectId ? "Leave" : "Unlink"}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select
            showSearch
            allowClear
            className="min-w-[260px]"
            placeholder="Add a wave…"
            optionFilterProp="label"
            options={options}
            value={toAdd}
            onChange={(v) => setToAdd(v ?? undefined)}
            disabled={unread}
            notFoundContent="No eligible survey projects"
          />
          {toAdd && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirm({ kind: "add" })}
              className="dq-btn"
            >
              <FontAwesomeIcon icon={faLink} className="h-3 w-3" />
              Add to tracker
            </button>
          )}
        </div>
      </div>

      {/* Tracker report */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Tracker report
        </div>
        <p className="text-[12px] leading-relaxed text-slate-400">
          A report of what changed from wave to wave across the whole tracker, as a CSV file: each
          wave compared with the one before it, ordered by when each wave last changed, oldest
          first.
        </p>
        <button
          type="button"
          onClick={handleExportReport}
          disabled={members.length < 2 || busy}
          className="dq-btn dq-btn--primary mt-2"
        >
          <FontAwesomeIcon icon={faFileCsv} />
          Export tracker report (CSV)
        </button>
        {members.length < 2 && (
          <p className="mt-1 text-[11px] text-slate-400">Add at least two waves to generate a report.</p>
        )}
      </div>

      <ConfirmationModal
        isVisible={!!confirm}
        title={confirm?.kind === "add" ? "Add to this tracker?" : isLeave ? "Leave this tracker?" : "Unlink this wave?"}
        message={
          confirm?.kind === "add"
            ? members.length === 0
              ? "This project becomes the root; the selected project joins as a wave."
              : "The selected project joins this tracker (moved from any tracker it was in)."
            : isLeave
            ? "This project will leave the tracker."
            : `“${confirmMember?.name || "This wave"}” will be unlinked from this tracker.`
        }
        confirmButtonText={confirm?.kind === "add" ? "Add" : isLeave ? "Leave" : "Unlink"}
        confirmButtonType={confirm?.kind === "add" ? "primary" : "danger"}
        cancel={() => setConfirm(null)}
        confirm={onConfirm}
      />
    </div>
  )
}

export default TrackerManager
